import React, { useEffect, useMemo, useState } from 'react';
import { Database, AlertCircle, Lock, RefreshCw, Download, FileText, Shield } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { roleService, userService } from '../../users';

const normalizeList = (payload, key) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.[key])) {
    return payload[key];
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const downloadText = (filename, content, contentType) => {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const toCsv = (rows) => {
  if (!rows.length) {
    return 'no_data';
  }

  const headers = Array.from(
    rows.reduce((acc, row) => {
      Object.keys(row || {}).forEach((key) => acc.add(key));
      return acc;
    }, new Set()),
  );

  const serialized = rows.map((row) =>
    headers
      .map((header) => {
        const value = row?.[header];
        const raw = value === undefined || value === null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
        return `"${raw.replace(/"/g, '""')}"`;
      })
      .join(','),
  );

  return [headers.join(','), ...serialized].join('\n');
};

const AdminSection = () => {
  const { user } = useApiAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [authorizationCode, setAuthorizationCode] = useState('');
  const [showAuthorizationModal, setShowAuthorizationModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  const canRunProtectedActions = useMemo(() => authorizationCode.trim().length > 0, [authorizationCode]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersPayload, rolesPayload] = await Promise.all([
        userService.getAllUsers(),
        roleService.getRoles(),
      ]);

      setUsers(normalizeList(usersPayload, 'users'));
      setRoles(normalizeList(rolesPayload, 'roles'));
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const exportData = (resource, format) => {
    const rows = resource === 'users' ? users : roles;

    if (format === 'json') {
      downloadText(`${resource}.json`, JSON.stringify(rows, null, 2), 'application/json');
      toast.success(`${resource} exported as JSON`);
      return;
    }

    downloadText(`${resource}.csv`, toCsv(rows), 'text/csv');
    toast.success(`${resource} exported as CSV`);
  };

  const showAuthorization = (action) => {
    setSelectedAction(action);
    setAuthorizationCode('');
    setShowAuthorizationModal(true);
  };

  const handleProtectedAction = async () => {
    if (!canRunProtectedActions) {
      toast.error('Authorization code is required');
      return;
    }

    try {
      setLoading(true);

      if (selectedAction === 'promote-self' && user?.id) {
        await userService.updateUserRole(user.id, 'admin');
        toast.success('Your role was updated to admin');
      }

      await loadData();
    } catch (error) {
      console.error('Protected admin action failed:', error);
      toast.error('Protected action failed');
    } finally {
      setLoading(false);
      setShowAuthorizationModal(false);
      setAuthorizationCode('');
      setSelectedAction(null);
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Database className="w-5 h-5" />
        Admin Operations (API Mode)
      </h3>

      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
          <div className="text-sm text-yellow-800">
            Legacy Firebase collection management is disabled. This panel now uses API-backed role/user operations.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium">Users</h4>
            <span className="text-sm text-gray-500">{users.length}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => exportData('users', 'json')}
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
            >
              <Download className="w-4 h-4" /> JSON
            </button>
            <button
              onClick={() => exportData('users', 'csv')}
              className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
            >
              <FileText className="w-4 h-4" /> CSV
            </button>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium">Roles</h4>
            <span className="text-sm text-gray-500">{roles.length}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => exportData('roles', 'json')}
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
            >
              <Download className="w-4 h-4" /> JSON
            </button>
            <button
              onClick={() => exportData('roles', 'csv')}
              className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
            >
              <FileText className="w-4 h-4" /> CSV
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4">
        <h4 className="font-medium mb-2">Protected Action</h4>
        <p className="text-sm text-gray-600 mb-4">Promote the current account to admin role through API role update.</p>
        <button
          onClick={() => showAuthorization('promote-self')}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-2"
          disabled={loading || !user?.id}
        >
          <Shield className="w-4 h-4" />
          Promote Current User
        </button>
      </div>

      <div className="flex justify-end">
        <button
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 border rounded hover:bg-gray-50 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {showAuthorizationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Authorization Required
            </h4>
            <p className="text-sm text-gray-600 mb-4">Enter authorization code to continue.</p>
            <input
              type="password"
              value={authorizationCode}
              onChange={(event) => setAuthorizationCode(event.target.value)}
              className="w-full px-3 py-2 border rounded-lg mb-4"
              placeholder="Authorization code"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAuthorizationModal(false)}
                className="px-3 py-2 text-sm border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleProtectedAction}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                disabled={loading}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSection;
